/**
 * Authentication service with modern API patterns
 */

import api, { apiEndpoints, tokenManager } from '../lib/api'
import { describeApiError } from '../lib/apiError'
import { STORAGE_KEYS } from '../lib/storageKeys'
import {
  clearAuthStorageByPrefix,
  getAuthStorageItem,
  removeAuthStorageItem,
  setAuthStorageItem,
} from '../lib/authStorage'
import type {
  FeatureFlags,
  LoginRequest,
  LoginResponse,
  User,
  UserProfile,
  UserPermissions,
} from '../types/auth'

/** Falha de login com a frase pronta e o erro do axios para quem decide o popup. */
export class LoginError extends Error {
  readonly original: unknown

  constructor(message: string, original: unknown) {
    super(message)
    this.name = 'LoginError'
    this.original = original
  }
}

class AuthService {
  private readonly PROFILE_CACHE_KEY = STORAGE_KEYS.PROFILE_CACHE
  private readonly PROFILE_CACHE_TTL = 60 * 60 * 1000 // 1 hour

  /**
   * Get profile cache from localStorage
   */
  private getProfileCache(): {
    user: User
    profile: UserProfile
    permissions: UserPermissions
    features: FeatureFlags | null
    timestamp: number
  } | null {
    try {
      const cached = getAuthStorageItem(this.PROFILE_CACHE_KEY)
      if (!cached) return null
      return JSON.parse(cached)
    } catch {
      return null
    }
  }

  /**
   * Set profile cache to localStorage
   */
  private setProfileCache(
    user: User,
    profile: UserProfile,
    permissions: UserPermissions,
    features: FeatureFlags | null = null
  ): void {
    try {
      const cacheData = {
        user,
        profile,
        permissions,
        features,
        timestamp: Date.now(),
      }
      setAuthStorageItem(this.PROFILE_CACHE_KEY, JSON.stringify(cacheData))
    } catch {
      // Ignore cache errors
    }
  }

  /**
   * Clear profile cache
   */
  private clearProfileCache(): void {
    removeAuthStorageItem(this.PROFILE_CACHE_KEY)
  }

  /**
   * Authenticate user with email and password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      // Clear any existing data before login
      this.clearAuthData()

      const response = await api.post<LoginResponse>(apiEndpoints.auth.login, {
        email,
        password,
      } as LoginRequest)

      const { access, refresh, user, profile, permissions, features } =
        response.data

      // Map backend snake_case profile to frontend camelCase if needed
      const rawProfile = profile as unknown as Record<string, unknown>
      const uiProfile: UserProfile = {
        allowed_agents: (rawProfile.allowed_agents ?? []) as string[],
        allowed_agents_display: (rawProfile.allowed_agents_display ??
          []) as string[],
        can_manage_documents: (rawProfile.can_manage_documents ??
          false) as boolean,
        department: (rawProfile.department ?? '') as string,
      }

      // Store tokens securely
      tokenManager.setTokens(access, refresh)

      // Set cache with fresh login data (including features)
      this.setProfileCache(user, uiProfile, permissions, features ?? null)

      // Store user data (for fallback)
      setAuthStorageItem(STORAGE_KEYS.USER, JSON.stringify(user))
      setAuthStorageItem(STORAGE_KEYS.PROFILE, JSON.stringify(uiProfile))
      setAuthStorageItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions))
      if (features) {
        setAuthStorageItem(STORAGE_KEYS.FEATURES, JSON.stringify(features))
      }

      return response.data
    } catch (error: unknown) {
      // Senha errada volta 401 (não 400), e o 429 é o limite de tentativas: os
      // dois caíam em "Verifique sua conexão". O erro original segue em `original`
      // para o formulário saber quando é o servidor que está fora.
      throw new LoginError(
        describeApiError(error, 'Erro ao fazer login.'),
        error
      )
    }
  }

  /**
   * Logout user and clear all stored data
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = tokenManager.getRefreshToken()

      if (refreshToken) {
        // Notify backend about logout (optional)
        await api.post(apiEndpoints.auth.logout, {
          refresh: refreshToken,
        })
      }
    } catch {
      // Ignore logout errors - we still want to clear local data
    } finally {
      // Always clear all authentication data
      this.clearAuthData()
    }
  }

  /**
   * Get current session data (cache/local only; no network)
   */
  async getSessionData(): Promise<{
    user: User
    profile: UserProfile
    permissions: UserPermissions
    features: FeatureFlags | null
  }> {
    // Prefer cache; do NOT hit network automatically to avoid redundant calls
    const profileCache = this.getProfileCache()
    if (profileCache) {
      const cacheAge = Date.now() - profileCache.timestamp
      if (cacheAge < this.PROFILE_CACHE_TTL) {
        return {
          user: profileCache.user,
          profile: profileCache.profile,
          permissions: profileCache.permissions,
          features: profileCache.features ?? null,
        }
      }
    }

    // Fallback to stored data (set at login)
    const stored = this.getStoredUserData()
    if (stored.user && stored.profile && stored.permissions) {
      // Migrate profile to camelCase if needed
      const rawProfile = stored.profile as unknown as Record<string, unknown>
      const uiProfile: UserProfile = {
        allowed_agents: (rawProfile.allowed_agents ?? []) as string[],
        allowed_agents_display: (rawProfile.allowed_agents_display ??
          []) as string[],
        can_manage_documents: (rawProfile.can_manage_documents ??
          false) as boolean,
        department: (rawProfile.department ?? '') as string,
      }

      // If migration happened, persist it
      try {
        setAuthStorageItem(STORAGE_KEYS.PROFILE, JSON.stringify(uiProfile))
      } catch {
        // Ignore storage errors
      }

      return {
        user: stored.user,
        profile: uiProfile,
        permissions: stored.permissions,
        features: stored.features ?? null,
      }
    }

    // If nothing available locally, signal error to let caller decide (e.g., logout)
    throw new Error('Dados de usuário indisponíveis localmente')
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = tokenManager.getAccessToken()
    if (!token) return false

    // Check if token is expired
    return !tokenManager.isTokenExpired(token)
  }

  /**
   * Get stored user data
   */
  getStoredUserData(): {
    user: User | null
    profile: UserProfile | null
    permissions: UserPermissions | null
    features: FeatureFlags | null
  } {
    try {
      const user = getAuthStorageItem(STORAGE_KEYS.USER)
      const profile = getAuthStorageItem(STORAGE_KEYS.PROFILE)
      const permissions = getAuthStorageItem(STORAGE_KEYS.PERMISSIONS)
      const features = getAuthStorageItem(STORAGE_KEYS.FEATURES)

      return {
        user: user ? JSON.parse(user) : null,
        profile: profile ? JSON.parse(profile) : null,
        permissions: permissions ? JSON.parse(permissions) : null,
        features: features ? JSON.parse(features) : null,
      }
    } catch {
      return {
        user: null,
        profile: null,
        permissions: null,
        features: null,
      }
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    // Clear tokens
    tokenManager.clearTokens()

    // Clear profile cache
    this.clearProfileCache()

    // Clear all user data from localStorage
    removeAuthStorageItem(STORAGE_KEYS.USER)
    removeAuthStorageItem(STORAGE_KEYS.PROFILE)
    removeAuthStorageItem(STORAGE_KEYS.PERMISSIONS)

    // Clear any agents cache too
    removeAuthStorageItem(STORAGE_KEYS.AGENTS_CACHE)

    // Clear any optimus-related keys (be aggressive)
    clearAuthStorageByPrefix('optimus_')
  }

  /**
   * Request password reset
   */
  async resetPassword(email: string): Promise<void> {
    const response = await api.post(apiEndpoints.auth.resetPassword, { email })
    return response.data
  }

  /**
   * Confirm password reset with token
   */
  async confirmResetPassword(
    uid: string,
    token: string,
    newPassword: string
  ): Promise<void> {
    const response = await api.post(apiEndpoints.auth.resetPasswordConfirm, {
      uid,
      token,
      new_password: newPassword,
    })
    return response.data
  }
}

export const authService = new AuthService()
export default authService
